import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useDropdownPosition from '../../../hooks/useDropdownPosition';

/**
 * ColumnFilter - Reusable column filter dropdown component
 * 
 * Props:
 * - columnId: Unique identifier for the column
 * - columnName: Display name for the column
 * - columnType: Type of column ('text', 'number', 'progress', 'date', etc.)
 * - isOpen: Whether the filter dropdown is currently open
 * - onToggle: Callback to toggle the filter open/closed
 * - selectedValues: Array of currently selected filter values
 * - uniqueValues: Array of unique values available for filtering
 * - onToggleValue: Callback when a value is toggled (columnId, value)
 * - onClear: Callback to clear all filters for this column
 * - onSelectAll: Callback to select all values for this column
 * - onTextFilter: Callback for text-based filtering (columnId, { mode, text })
 * - textFilter: Current text filter state { mode, text }
 */
const ColumnFilter = ({ 
  columnId, 
  columnName,
  columnType = 'text',
  isOpen,
  onToggle,
  selectedValues = [],
  uniqueValues = [],
  onToggleValue,
  onClear,
  onSelectAll,
  onTextFilter,
  textFilter
}) => {
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);
  const filterInputRef = useRef(null);
  const [alignRight, setAlignRight] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterMode, setFilterMode] = useState(textFilter?.mode || 'contains');
  const [filterTextValue, setFilterTextValue] = useState(textFilter?.text || '');
  const [activeTab, setActiveTab] = useState('values'); // 'values' or 'search'
  const getPosition = useDropdownPosition({ menuHeight: 400, menuWidth: 280 });

  // Only sync when dropdown opens (not on every render)
  useEffect(() => {
    if (isOpen) {
      setFilterMode(textFilter?.mode || 'contains');
      setFilterTextValue(textFilter?.text || '');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if dropdown should align to the right or open upward to avoid overflow
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const pos = getPosition(buttonRef.current);
      setOpenUpward(pos.openUpward);
      setAlignRight(pos.right !== undefined);
    }
  }, [isOpen, getPosition]);

  const handleButtonClick = (e) => {
    e.stopPropagation();
    onToggle(columnId);
  };

  const handleBackdropClick = (e) => {
    e.stopPropagation();
    onToggle(null);
  };

  // Close on ESC key press
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onToggle(null);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onToggle]);

  const handleSelectAll = () => {
    onSelectAll(columnId, filteredValues);
  };

  const handleClear = () => {
    onClear(columnId);
    setSearchText('');
    setFilterTextValue('');
    if (onTextFilter) {
      onTextFilter(columnId, { mode: 'contains', text: '' });
    }
  };

  const handleToggleValue = (value) => {
    onToggleValue(columnId, value);
  };

  const handleApplyTextFilter = () => {
    if (onTextFilter && filterTextValue.trim()) {
      onTextFilter(columnId, { mode: filterMode, text: filterTextValue.trim() });
    }
  };

  const handleClearTextFilter = () => {
    setFilterTextValue('');
    if (onTextFilter) {
      onTextFilter(columnId, { mode: filterMode, text: '' });
    }
  };

  // Filter the unique values based on search text
  const filteredValues = searchText 
    ? uniqueValues.filter(v => v.toLowerCase().includes(searchText.toLowerCase()))
    : uniqueValues;

  // Determine if this is a numeric column
  const isNumericColumn = columnType === 'number' || columnType === 'progress' || columnType === 'progress-readonly';

  // Text filter modes
  const textFilterModes = [
    { value: 'contains', label: 'Contains' },
    { value: 'equals', label: 'Equals' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'not_equals', label: 'Is not equal to' },
    { value: 'starts_with', label: 'Starts with' },
    { value: 'ends_with', label: 'Ends with' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' }
  ];

  // Numeric filter modes
  const numericFilterModes = [
    { value: 'equals', label: 'Equals (=)' },
    { value: 'not_equals', label: 'Not equals (≠)' },
    { value: 'greater_than', label: 'Greater than (>)' },
    { value: 'greater_or_equal', label: 'Greater or equal (≥)' },
    { value: 'less_than', label: 'Less than (<)' },
    { value: 'less_or_equal', label: 'Less or equal (≤)' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' }
  ];

  // Use appropriate filter modes based on column type
  const filterModes = isNumericColumn ? numericFilterModes : textFilterModes;

  const hasActiveFilter = selectedValues.length > 0 || (textFilter && textFilter.text);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        style={{
          marginLeft: '8px',
          padding: '2px 4px',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center'
        }}
        title="Filter column"
      >
        <svg 
          width="14" 
          height="14" 
          viewBox="0 0 16 16" 
          fill={hasActiveFilter ? '#007bff' : '#666'}
          style={{ display: 'block' }}
        >
          <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z"/>
        </svg>
      </button>
      
      {isOpen && createPortal(
        <>
          {/* Backdrop to close filter when clicking outside */}
          <div
            onClick={handleBackdropClick}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 99999,
              backgroundColor: 'transparent'
            }}
          />
          
          {/* Filter dropdown */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              ...(openUpward
                ? { bottom: buttonRef.current ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 4}px` : 'auto' }
                : { top: buttonRef.current ? `${buttonRef.current.getBoundingClientRect().bottom + 4}px` : '0' }),
              left: alignRight ? 'auto' : (buttonRef.current ? `${buttonRef.current.getBoundingClientRect().left}px` : '0'),
              right: alignRight ? (buttonRef.current ? `${window.innerWidth - buttonRef.current.getBoundingClientRect().right}px` : 'auto') : 'auto',
              backgroundColor: 'white',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 100000,
              minWidth: '280px',
              maxHeight: '400px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
                Filter: {columnName}
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6' }}>
              <button
                onClick={() => setActiveTab('values')}
                style={{
                  flex: 1,
                  padding: '8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: activeTab === 'values' ? 'white' : '#f8f9fa',
                  border: 'none',
                  borderBottom: activeTab === 'values' ? '2px solid #10b981' : '2px solid transparent',
                  cursor: 'pointer',
                  color: activeTab === 'values' ? '#10b981' : '#666'
                }}
              >
                Select Values
              </button>
              <button
                onClick={() => setActiveTab('search')}
                style={{
                  flex: 1,
                  padding: '8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: activeTab === 'search' ? 'white' : '#f8f9fa',
                  border: 'none',
                  borderBottom: activeTab === 'search' ? '2px solid #10b981' : '2px solid transparent',
                  cursor: 'pointer',
                  color: activeTab === 'search' ? '#10b981' : '#666'
                }}
              >
                Text Search
              </button>
            </div>

            {activeTab === 'values' ? (
              /* Values Tab */
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                {/* Search within values */}
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search values..."
                    defaultValue={searchText}
                    autoComplete="off"
                    onInput={(e) => setSearchText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#10b981'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  />
                </div>

                {/* Select All / Clear buttons */}
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #eee', display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleSelectAll}
                    style={{
                      padding: '5px 10px',
                      fontSize: '11px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Select All ({filteredValues.length})
                  </button>
                  <button
                    onClick={handleClear}
                    style={{
                      padding: '5px 10px',
                      fontSize: '11px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Clear All
                  </button>
                </div>
                
                {/* Values list */}
                <div style={{ flex: 1, overflow: 'auto', padding: '4px 8px', maxHeight: '200px' }}>
                  {filteredValues.length === 0 ? (
                    <div style={{ padding: '12px', color: '#6c757d', fontSize: '12px', textAlign: 'center' }}>
                      {searchText ? 'No matching values' : 'No values available'}
                    </div>
                  ) : (
                    filteredValues.map(value => (
                      <label
                        key={value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          borderRadius: '4px',
                          marginBottom: '2px',
                          backgroundColor: selectedValues.includes(value) ? '#d1fae5' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = selectedValues.includes(value) ? '#a7f3d0' : '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedValues.includes(value) ? '#d1fae5' : 'transparent'}
                      >
                        <input
                          type="checkbox"
                          checked={selectedValues.includes(value)}
                          onChange={() => handleToggleValue(value)}
                          style={{ marginRight: '8px', cursor: 'pointer', accentColor: '#10b981' }}
                        />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {value}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            ) : (
              /* Text Search Tab */
              <div style={{ padding: '12px' }}>
                {/* Filter Mode Dropdown */}
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    Filter Mode
                  </label>
                  <select
                    value={filterMode}
                    onChange={(e) => setFilterMode(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      fontSize: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#10b981'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  >
                    {filterModes.map(mode => (
                      <option key={mode.value} value={mode.value}>
                        {mode.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search Text Input */}
                {!['is_empty', 'is_not_empty'].includes(filterMode) && (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>
                      Search Text
                    </label>
                    <input
                      ref={filterInputRef}
                      type="text"
                      placeholder="Enter search text..."
                      defaultValue={filterTextValue}
                      autoComplete="off"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onInput={(e) => setFilterTextValue(e.target.value)}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          handleApplyTextFilter();
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        fontSize: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#10b981'}
                      onBlur={(e) => e.target.style.borderColor = '#ddd'}
                    />
                  </div>
                )}

                {/* Apply / Clear buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleApplyTextFilter}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Apply Filter
                  </button>
                  <button
                    onClick={handleClearTextFilter}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Clear
                  </button>
                </div>

                {/* Active filter indicator */}
                {textFilter && textFilter.text && (
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '8px', 
                    backgroundColor: '#d1fae5', 
                    borderRadius: '4px',
                    fontSize: '11px',
                    color: '#065f46'
                  }}>
                    <strong>Active filter:</strong> {filterModes.find(m => m.value === textFilter.mode)?.label} "{textFilter.text}"
                  </div>
                )}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default ColumnFilter;
















